<template lang="pug">
span.field-hint(@mouseover="hintMouseover($event)", @mouseleave="hintMouseleave()")
  q-icon.hint-icon(name="help")
  p.hint-text(:class="{ 'hint-text--top': hintPlacement === 'top' }", :style="hintStyles") {{ text }}
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  text: { type: String, default: "", required: true },
});

const emit = defineEmits(["change", "reset"]);

const showText = ref(false);
const hintPositionX = ref(0);
const hintPositionY = ref(0);
const hintTranslateX = ref("0px");
const hintArrowTranslateX = ref("0px");
const hintPlacement = ref("bottom");

const arrowHeight = 8;
const hintGap = 4;
const viewportPadding = 12;

const text = computed(() => props.text);
const hintStyles = computed(() => {
  return {
    visibility: `${showText.value ? "visible" : "hidden"}`,
    left: `${hintPositionX.value}px`,
    top: `${hintPositionY.value}px`,
    "--translateX": hintTranslateX.value,
    "--arrowTranslateX": hintArrowTranslateX.value,
  };
});

const hintMouseover = e => {
  const hintEl = e.currentTarget.querySelector(".hint-icon");
  const hintRect = hintEl.getBoundingClientRect();
  const hintTextEl = e.currentTarget.querySelector(".hint-text");
  const hintTextRect = hintTextEl.getBoundingClientRect();
  const hintCenterX = hintRect.left + hintRect.width / 2;
  const requiredVerticalSpace = hintTextRect.height + arrowHeight + hintGap;
  const hasSpaceBelow = window.innerHeight - hintRect.bottom >= requiredVerticalSpace + viewportPadding;

  hintPositionX.value = hintCenterX;
  hintPlacement.value = hasSpaceBelow ? "bottom" : "top";
  hintPositionY.value = hasSpaceBelow
    ? hintRect.bottom + arrowHeight + hintGap
    : Math.max(viewportPadding, hintRect.top - requiredVerticalSpace);

  const hintLeft = hintCenterX - hintTextRect.width / 2;
  const hintRight = hintCenterX + hintTextRect.width / 2;

  const horizontalShift =
    hintLeft < viewportPadding
      ? viewportPadding - hintLeft
      : hintRight > window.innerWidth - viewportPadding
      ? window.innerWidth - viewportPadding - hintRight
      : 0;

  hintTranslateX.value = `${horizontalShift}px`;
  hintArrowTranslateX.value = `${-horizontalShift}px`;

  showText.value = true;
};

const hintMouseleave = () => {
  hintPositionX.value = 0;
  hintPositionY.value = 0;
  hintTranslateX.value = "0px";
  hintArrowTranslateX.value = "0px";
  hintPlacement.value = "bottom";

  showText.value = false;
};

onMounted(() => {
  window.addEventListener("scroll", hintMouseleave, true);
  window.addEventListener("resize", hintMouseleave);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", hintMouseleave, true);
  window.removeEventListener("resize", hintMouseleave);
});
</script>

<style lang="scss">
.field-hint {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  cursor: pointer;
}

.hint-text {
  position: fixed;
  width: 400px;
  max-width: calc(100vw - 24px);
  height: auto;
  box-sizing: border-box;
  padding: 12px 16px;
  margin: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
  background-color: #343442;
  color: #e8e8e8;
  z-index: 9999;
  box-shadow: 0 1px 2px rgba(89, 89, 89, 0.16), 0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 8px rgba(73, 73, 73, 0.1);
  border-radius: 5px;
  pointer-events: none;
  transform: translateX(calc(-50% + var(--translateX, 0px)));

  &::before {
    content: "";
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #343442;
    left: 50%;
    transform: translateX(calc(-50% + var(--arrowTranslateX, 0px)));
    top: -8px;
    position: absolute;
  }

  &--top::before {
    top: auto;
    bottom: -8px;
    border-top: 8px solid #343442;
    border-bottom: 0;
  }
}
</style>
